import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { KINDS, PLACES, CITIES, Kind } from '../data/smaken';
import { PlaceCard } from '../components/PlaceCard';
import { Reveal } from '../components/Reveal';
import { Note } from '../components/Marks';
const AREAS = Object.keys(CITIES);
export function Discover() {
  const [params, setParams] = useSearchParams();
  const kind = params.get('kategori') as Kind | null;
  const query = params.get('q') ?? '';
  const area = params.get('ort') ?? '';
  const onlyNew = params.get('filter') === 'nytt';
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);else next.delete(key);
    setParams(next, {
      replace: true
    });
  };
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PLACES.filter(place => {
      if (kind && place.kind !== kind) return false;
      if (area && place.city !== area) return false;
      if (onlyNew && !place.isNew) return false;
      if (!q) return true;
      const haystack = [place.name, place.city, place.blurb, ...place.tags].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [kind, query, area, onlyNew]);
  const activeKind = KINDS.find(k => k.id === kind);
  const heading = onlyNew ? 'NYTT' : activeKind ? activeKind.label : 'UPPTÄCK';
  const sub = onlyNew ? 'Nya platser att upptäcka' : activeKind ? activeKind.sub : 'Allt vi kan stå för i Småland.';
  return <div className="mx-auto max-w-page px-5 py-12 md:px-8 md:py-16" data-id="element-900" data-mp="26isboszhlrbg">
      <Reveal data-id="element-901" data-mp="26l65yzmcezhy">
        <h1 className="display-xl text-5xl uppercase sm:text-7xl" data-id="element-902" data-mp="21jyxeycdlwa8">{heading}</h1>
        <p className="mt-3 text-lg text-black/60" data-id="element-903" data-mp="21m1xycb5n7t7">{sub}</p>
      </Reveal>

      {/* Controls */}
      <Reveal delay={0.05} data-id="element-904" data-mp="4qxdng65llbp">
        <div className="mt-10 border-t-2 border-black pt-5" data-id="element-905" data-mp="3vy04u7lzrrlx">
          <div className="flex items-center gap-3 border-b border-black/15 pb-4" data-id="element-906" data-mp="3vyrr2rtp8czj">
            <SearchIcon className="h-5 w-5 shrink-0 text-black/40" aria-hidden="true" data-id="element-907" data-mp="iwfmi8myii91" />
            <label htmlFor="discover-q" className="sr-only" data-id="element-908" data-mp="noz7vrrvyhoe">
              Sök
            </label>
            <input id="discover-q" value={query} onChange={e => setParam('q', e.target.value)} placeholder="Bageri, lunch, gårdsbutik, kaffe…" className="w-full bg-transparent font-display text-xl outline-none placeholder:text-black/30 sm:text-2xl" data-id="element-909" data-mp="9xlqokhg9vwj" />
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-910" data-mp="16kgzbtr64ku9">
            <button onClick={() => setParams(new URLSearchParams(query ? {
            q: query
          } : {}), {
            replace: true
          })} aria-pressed={!kind && !onlyNew} className={`border px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out ${!kind && !onlyNew ? 'border-black bg-black text-papper' : 'border-black/20 hover:border-black'}`} data-id="element-911" data-mp="smikkn8xqme8">
              Alla
            </button>
            {KINDS.map(k => <button key={k.id} onClick={() => setParam('kategori', kind === k.id ? '' : k.id)} aria-pressed={kind === k.id} className={`border px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 transition-colors duration-150 ease-out ${kind === k.id ? 'border-black bg-black text-papper' : 'border-black/20 hover:border-black'}`} data-id="element-912" data-mp="3ju1efqflnvvy">
                {k.label}
              </button>)}

            <div className="relative ml-auto" data-id="element-913" data-mp="2w2vy7a5qz1l4">
              <label htmlFor="discover-area" className="sr-only" data-id="element-914" data-mp="2w241tx4bszts">
                Ort
              </label>
              <select id="discover-area" value={area} onChange={e => setParam('ort', e.target.value)} className="cursor-pointer border border-black/20 bg-transparent px-4 py-2 text-[11px] font-bold uppercase tracking-wide2 outline-none transition-colors duration-150 ease-out hover:border-black" data-id="element-915" data-mp="23f9k37yw8ek0">
                <option value="" data-id="element-916" data-mp="2qy3bq2p13yv6">Hela Småland</option>
                {AREAS.map(a => <option key={a} value={a} data-id="element-917" data-mp="2vs7jgo64caxb">
                    {a}
                  </option>)}
              </select>
            </div>
          </div>
        </div>
      </Reveal>

      <p className="mt-8 flex items-baseline gap-3" data-id="element-918" data-mp="34pq8s4qspbf0">
        <span className="text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-919" data-mp="39q4ehg4xc99v">
          {results.length} ställen
        </span>
        {results.length > 0 && <Note className="!text-lg" data-id="element-920" data-mp="3eneflt1oe9dj">Alla är värda resan.</Note>}
      </p>

      {results.length === 0 ? <div className="mt-8 border-y-2 border-black py-20 text-center" data-id="element-921" data-mp="1fezeclt1qeza">
          <p className="display-xl text-3xl uppercase" data-id="element-922" data-mp="1kbzmr3ddbttk">Inget här. Än.</p>
          <p className="mt-3 text-black/60" data-id="element-923" data-mp="1hmzgh0gxnqd">Prova en annan kategori eller en annan ort.</p>
        </div> : <div className="mt-8 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-id="element-924" data-mp="3stpvlnlcnxtj">
          {results.map((place, i) => <Reveal key={place.slug} delay={Math.min(i * 0.04, 0.2)} data-id="element-925" data-mp="3orbb6z218q05" data-mpi={i}>
              <PlaceCard place={place} data-id="element-926" data-mp="3jw5qg3kd9hdn" />
            </Reveal>)}
        </div>}
    </div>;
}